import React, { useState } from 'react';
import '../components/BoardColumnFilter.css';
import './BoardSearchFilters.css';
import { BoardSearchFilterPopoverContent } from './BoardSearchFilterPopoverContent';

export function EmAbertoColumnFilterPanel({
  variant = 'default',
  filterType,
  onClose,
  selectedColumnFilters,
  setSelectedColumnFilters,
  onApplyLive,
  citiesWithCounts,
  bairrosWithCounts,
  clientesWithCounts,
  tiposOSWithCounts,
  slasWithCounts,
  equipamentosWithCounts,
  statusWithCounts,
  tecnicoInicialWithCounts,
  defeitoDescricaoWithCounts,
  defeitoAlertWithCounts,
  defeitoClassificationWithCounts,
  defeitoSeverityWithCounts,
}) {
    const [tempFilters, setTempFilters] = useState({ ...selectedColumnFilters });
    const [searchTerm, setSearchTerm] = useState('');

    const commitFilters = (next) => {
      setTempFilters(next);
      if (variant === 'popover' && onApplyLive) {
        onApplyLive(next);
      }
    };

    const availableCities = React.useMemo(() => {
      return citiesWithCounts.map(item => item.cidade);
    }, [citiesWithCounts]);

    const availableBairros = React.useMemo(() => {
      return bairrosWithCounts.map(item => item.bairro);
    }, [bairrosWithCounts]);

    const availableClientes = React.useMemo(() => {
      return clientesWithCounts.map(item => item.cliente);
    }, [clientesWithCounts]);

    const availableSLAs = React.useMemo(() => {
      return slasWithCounts.map(item => item.sla);
    }, [slasWithCounts]);

    const availableEquipamentos = React.useMemo(() => {
      return equipamentosWithCounts.map(item => item.equipamento);
    }, [equipamentosWithCounts]);

    const availableStatus = React.useMemo(() => {
      return statusWithCounts.map(item => item.status);
    }, [statusWithCounts]);

    const availableTecnicoInicial = React.useMemo(() => {
      return tecnicoInicialWithCounts.map(item => item.tecnicoInicial);
    }, [tecnicoInicialWithCounts]);

    const filteredCities = React.useMemo(() => {
      if (!searchTerm) return availableCities;
      return availableCities.filter(cidade => 
        cidade.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [availableCities, searchTerm]);

    const filteredBairros = React.useMemo(() => {
      const base = [...bairrosWithCounts].sort((a, b) => b.count - a.count);
      if (!searchTerm) return base;
      return base.filter((item) =>
        item.bairro.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [bairrosWithCounts, searchTerm]);

    const filteredClientes = React.useMemo(() => {
      const base = [...clientesWithCounts].sort((a, b) => b.count - a.count);
      if (!searchTerm) return base;
      return base.filter((item) =>
        item.cliente.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [clientesWithCounts, searchTerm]);

    const filteredEquipamentos = React.useMemo(() => {
      const base = [...equipamentosWithCounts].sort((a, b) => b.count - a.count);
      if (!searchTerm) return base;
      return base.filter((item) =>
        item.equipamento.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [equipamentosWithCounts, searchTerm]);

    const filteredStatus = React.useMemo(() => {
      const base = [...statusWithCounts].sort((a, b) => b.count - a.count);
      if (!searchTerm) return base;
      return base.filter((item) =>
        item.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [statusWithCounts, searchTerm]);

    const filteredTecnicoInicial = React.useMemo(() => {
      const base = [...tecnicoInicialWithCounts].sort((a, b) => b.count - a.count);
      if (!searchTerm) return base;
      return base.filter((item) =>
        item.tecnicoInicial.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [tecnicoInicialWithCounts, searchTerm]);

    const filteredDefeitoDescricao = React.useMemo(() => {
      if (!searchTerm) return defeitoDescricaoWithCounts;
      const t = searchTerm.toLowerCase();
      return defeitoDescricaoWithCounts.filter((item) => item.value.toLowerCase().includes(t));
    }, [defeitoDescricaoWithCounts, searchTerm]);

    const filteredDefeitoAlert = React.useMemo(() => {
      if (!searchTerm) return defeitoAlertWithCounts;
      const t = searchTerm.toLowerCase();
      return defeitoAlertWithCounts.filter((item) => item.value.toLowerCase().includes(t));
    }, [defeitoAlertWithCounts, searchTerm]);

    const filteredDefeitoClassification = React.useMemo(() => {
      if (!searchTerm) return defeitoClassificationWithCounts;
      const t = searchTerm.toLowerCase();
      return defeitoClassificationWithCounts.filter((item) => item.value.toLowerCase().includes(t));
    }, [defeitoClassificationWithCounts, searchTerm]);

    const filteredDefeitoSeverity = React.useMemo(() => {
      if (!searchTerm) return defeitoSeverityWithCounts;
      const t = searchTerm.toLowerCase();
      return defeitoSeverityWithCounts.filter((item) => item.value.toLowerCase().includes(t));
    }, [defeitoSeverityWithCounts, searchTerm]);

    // Ãrvore UF -> cidades para filtro "UF e cidade"
    const stateTree = React.useMemo(() => {
      const byState = {};
      citiesWithCounts.forEach(item => {
        const e = String(item.estado || '').trim() || '(Sem UF)';
        if (!byState[e]) byState[e] = [];
        byState[e].push({ cidade: item.cidade, count: item.count });
      });
      return Object.entries(byState)
        .map(([estado, cities]) => ({
          estado,
          cities: [...cities].sort((a, b) => b.count - a.count),
        }))
        .sort((a, b) => {
          const sumA = a.cities.reduce((acc, c) => acc + c.count, 0);
          const sumB = b.cities.reduce((acc, c) => acc + c.count, 0);
          return sumB - sumA;
        });
    }, [citiesWithCounts]);

    const filteredStateTree = React.useMemo(() => {
      if (!searchTerm.trim()) return stateTree;
      const t = searchTerm.toLowerCase().trim();
      return stateTree
        .map(({ estado, cities }) => {
          const estadoMatch = estado.toLowerCase().includes(t);
          const filteredCities = cities.filter(c => c.cidade.toLowerCase().includes(t));
          if (estadoMatch) return { estado, cities };
          if (filteredCities.length > 0) return { estado, cities: filteredCities };
          return null;
        })
        .filter(Boolean);
    }, [stateTree, searchTerm]);

    const [expandedEstados, setExpandedEstados] = useState(() => new Set());
    React.useEffect(() => {
      if (filterType === 'cidade' && stateTree.length > 0) {
        setExpandedEstados(prev => {
          const next = new Set(prev);
          stateTree.forEach(s => next.add(s.estado));
          return next;
        });
      }
    }, [filterType, stateTree]);

    // Mapa UF -> lista completa de cidades (para permitir selecionar a UF e marcar todas as cidades)
    const citiesByEstado = React.useMemo(() => {
      const map = {};
      stateTree.forEach(s => {
        map[s.estado] = s.cities.map(c => c.cidade);
      });
      return map;
    }, [stateTree]);

    const getCityOrderCount = (cidade) => {
      const cityInfo = citiesWithCounts.find(item => item.cidade === cidade);
      return cityInfo ? cityInfo.count : 0;
    };

    const getBairroOrderCount = (bairro) => {
      const bairroInfo = bairrosWithCounts.find(item => item.bairro === bairro);
      return bairroInfo ? bairroInfo.count : 0;
    };

    const getClienteOrderCount = (cliente) => {
      const clienteInfo = clientesWithCounts.find(item => item.cliente === cliente);
      return clienteInfo ? clienteInfo.count : 0;
    };

    const getTipoOSOrderCount = (tipo) => {
      const tipoInfo = tiposOSWithCounts.find(item => item.tipo === tipo);
      return tipoInfo ? tipoInfo.count : 0;
    };

    const getSLAOrderCount = (sla) => {
      const slaInfo = slasWithCounts.find(item => item.sla === sla);
      return slaInfo ? slaInfo.count : 0;
    };

    const getEquipamentoOrderCount = (equipamento) => {
      const equipamentoInfo = equipamentosWithCounts.find(item => item.equipamento === equipamento);
      return equipamentoInfo ? equipamentoInfo.count : 0;
    };

    const getStatusOrderCount = (status) => {
      const statusInfo = statusWithCounts.find(item => item.status === status);
      return statusInfo ? statusInfo.count : 0;
    };

    const getTecnicoInicialOrderCount = (tecnicoInicial) => {
      const tecnicoInicialInfo = tecnicoInicialWithCounts.find(item => item.tecnicoInicial === tecnicoInicial);
      return tecnicoInicialInfo ? tecnicoInicialInfo.count : 0;
    };

    const getDefeitoDescricaoCount = (value) =>
      defeitoDescricaoWithCounts.find((item) => item.value === value)?.count ?? 0;
    const getDefeitoAlertCount = (value) =>
      defeitoAlertWithCounts.find((item) => item.value === value)?.count ?? 0;
    const getDefeitoClassificationCount = (value) =>
      defeitoClassificationWithCounts.find((item) => item.value === value)?.count ?? 0;
    const getDefeitoSeverityCount = (value) =>
      defeitoSeverityWithCounts.find((item) => item.value === value)?.count ?? 0;

    const toggleCityFilter = (cidade) => {
      const newCidades = tempFilters.cidade.includes(cidade)
        ? tempFilters.cidade.filter((c) => c !== cidade)
        : [...tempFilters.cidade, cidade];
      commitFilters({ ...tempFilters, cidade: newCidades });
    };

    const toggleEstadoFilter = (estado) => {
      const stateCities = citiesByEstado[estado] || [];
      if (stateCities.length === 0) return;

      const stateCitiesSet = new Set(stateCities);
      const allSelected = stateCities.every((c) => tempFilters.cidade.includes(c));

      const nextCidades = allSelected
        ? tempFilters.cidade.filter((c) => !stateCitiesSet.has(c))
        : Array.from(new Set([...tempFilters.cidade, ...stateCities]));

      commitFilters({ ...tempFilters, cidade: nextCidades });
    };

    const setCidadeSelection = (cidades) => {
      commitFilters({ ...tempFilters, cidade: cidades });
    };

    const toggleBairroFilter = (bairro) => {
      const newBairros = tempFilters.bairro.includes(bairro)
        ? tempFilters.bairro.filter((b) => b !== bairro)
        : [...tempFilters.bairro, bairro];
      commitFilters({ ...tempFilters, bairro: newBairros });
    };

    const toggleClienteFilter = (cliente) => {
      const newClientes = tempFilters.cliente.includes(cliente)
        ? tempFilters.cliente.filter((c) => c !== cliente)
        : [...tempFilters.cliente, cliente];
      commitFilters({ ...tempFilters, cliente: newClientes });
    };

    const toggleTipoOSFilter = (tipo) => {
      const newTiposOS = tempFilters.tipoOS.includes(tipo)
        ? tempFilters.tipoOS.filter((t) => t !== tipo)
        : [...tempFilters.tipoOS, tipo];
      commitFilters({ ...tempFilters, tipoOS: newTiposOS });
    };

    const toggleSLAFilter = (sla) => {
      const newSLAs = tempFilters.sla.includes(sla)
        ? tempFilters.sla.filter((s) => s !== sla)
        : [...tempFilters.sla, sla];
      commitFilters({ ...tempFilters, sla: newSLAs });
    };

    const toggleEquipamentoFilter = (equipamento) => {
      const newEquipamentos = tempFilters.equipamento.includes(equipamento)
        ? tempFilters.equipamento.filter((e) => e !== equipamento)
        : [...tempFilters.equipamento, equipamento];
      commitFilters({ ...tempFilters, equipamento: newEquipamentos });
    };

    const toggleStatusFilter = (status) => {
      const newStatus = tempFilters.status.includes(status)
        ? tempFilters.status.filter((s) => s !== status)
        : [...tempFilters.status, status];
      commitFilters({ ...tempFilters, status: newStatus });
    };

    const toggleTecnicoInicialFilter = (tecnicoInicial) => {
      const newTecnicoInicial = tempFilters.tecnicoInicial.includes(tecnicoInicial)
        ? tempFilters.tecnicoInicial.filter((t) => t !== tecnicoInicial)
        : [...tempFilters.tecnicoInicial, tecnicoInicial];
      commitFilters({ ...tempFilters, tecnicoInicial: newTecnicoInicial });
    };

    const td = (arr) => (Array.isArray(arr) ? arr : []);
    const toggleDefeitoDescricaoFilter = (value) => {
      const cur = td(tempFilters.defeitoDescricao);
      commitFilters({
        ...tempFilters,
        defeitoDescricao: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value],
      });
    };
    const toggleDefeitoAlertFilter = (value) => {
      const cur = td(tempFilters.defeitoAlert);
      commitFilters({
        ...tempFilters,
        defeitoAlert: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value],
      });
    };
    const toggleDefeitoClassificationFilter = (value) => {
      const cur = td(tempFilters.defeitoClassification);
      commitFilters({
        ...tempFilters,
        defeitoClassification: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value],
      });
    };
    const toggleDefeitoSeverityFilter = (value) => {
      const cur = td(tempFilters.defeitoSeverity);
      commitFilters({
        ...tempFilters,
        defeitoSeverity: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value],
      });
    };

    const applyFilters = () => {
      setSelectedColumnFilters(tempFilters);
      onClose();
    };

    const clearFilters = () => {
      if (filterType === 'retorno') {
        setTempFilters({ ...tempFilters, retorno: null });
      } else if (filterType === 'cidade') {
        setTempFilters({ ...tempFilters, cidade: [] });
      } else if (filterType === 'bairro') {
        setTempFilters({ ...tempFilters, bairro: [] });
      } else if (filterType === 'cliente') {
        setTempFilters({ ...tempFilters, cliente: [] });
      } else if (filterType === 'tipoOS') {
        setTempFilters({ ...tempFilters, tipoOS: [] });
      } else if (filterType === 'sla') {
        setTempFilters({ ...tempFilters, sla: [] });
      } else if (filterType === 'equipamento') {
        setTempFilters({ ...tempFilters, equipamento: [] });
      } else if (filterType === 'status') {
        setTempFilters({ ...tempFilters, status: [] });
      } else if (filterType === 'tecnicoInicial') {
        setTempFilters({ ...tempFilters, tecnicoInicial: [] });
      } else if (filterType === 'defeitoDescricao') {
        setTempFilters({ ...tempFilters, defeitoDescricao: [] });
      } else if (filterType === 'defeitoAlert') {
        setTempFilters({ ...tempFilters, defeitoAlert: [] });
      } else if (filterType === 'defeitoClassification') {
        setTempFilters({ ...tempFilters, defeitoClassification: [] });
      } else if (filterType === 'defeitoSeverity') {
        setTempFilters({ ...tempFilters, defeitoSeverity: [] });
      }
      setSearchTerm(''); // Limpar busca para mostrar todos os itens
    };

    const selectAll = () => {
      if (filterType === 'cidade') {
        const allCidades = filteredStateTree.flatMap(s => s.cities.map(c => c.cidade));
        setTempFilters({
          ...tempFilters,
          cidade: allCidades
        });
      } else if (filterType === 'bairro') {
        setTempFilters({
          ...tempFilters,
          bairro: filteredBairros.map((item) => item.bairro),
        });
      } else if (filterType === 'cliente') {
        setTempFilters({
          ...tempFilters,
          cliente: filteredClientes.map((item) => item.cliente),
        });
      } else if (filterType === 'tipoOS') {
        const allTipos = tiposOSWithCounts.map(item => item.tipo);
        setTempFilters({
          ...tempFilters,
          tipoOS: [...allTipos]
        });
      } else if (filterType === 'sla') {
        const allSLAs = slasWithCounts.map(item => item.sla);
        setTempFilters({
          ...tempFilters,
          sla: [...allSLAs]
        });
      } else if (filterType === 'equipamento') {
        setTempFilters({
          ...tempFilters,
          equipamento: [...filteredEquipamentos.map(item => item.equipamento)]
        });
      } else if (filterType === 'status') {
        const allStatus = statusWithCounts.map(item => item.status);
        setTempFilters({
          ...tempFilters,
          status: [...allStatus]
        });
      } else if (filterType === 'defeitoDescricao') {
        setTempFilters({ ...tempFilters, defeitoDescricao: filteredDefeitoDescricao.map((x) => x.value) });
      } else if (filterType === 'defeitoAlert') {
        setTempFilters({ ...tempFilters, defeitoAlert: filteredDefeitoAlert.map((x) => x.value) });
      } else if (filterType === 'defeitoClassification') {
        setTempFilters({ ...tempFilters, defeitoClassification: filteredDefeitoClassification.map((x) => x.value) });
      } else if (filterType === 'defeitoSeverity') {
        setTempFilters({ ...tempFilters, defeitoSeverity: filteredDefeitoSeverity.map((x) => x.value) });
      }
    };

    const getFilterTitle = () => {
      switch(filterType) {
        case 'retorno': return 'Filtrar por Retorno';
        case 'cidade': return 'Filtrar por UF e cidade';
        case 'bairro': return 'Filtrar por Bairro';
        case 'cliente': return 'Filtrar por Cliente';
        case 'tipoOS': return 'Filtrar por Tipo de OS';
        case 'sla': return 'Filtrar por SLA';
        case 'equipamento': return 'Filtrar por Equipamento';
        case 'status': return 'Filtrar por Status';
        case 'tecnicoInicial': return 'Filtrar por TÃ©cnico inicial';
        case 'defeitoDescricao': return 'Filtrar por defeito (descriÃ§Ã£o)';
        case 'defeitoAlert': return 'Filtrar por cÃ³digo de alerta';
        case 'defeitoClassification': return 'Filtrar por classificaÃ§Ã£o';
        case 'defeitoSeverity': return 'Filtrar por criticidade';
        default: return 'Filtro';
      }
    };

    const hasActiveFilters = () => {
      return (
        selectedColumnFilters.retorno === 'sim' ||
        selectedColumnFilters.retorno === 'nao' ||
        selectedColumnFilters.cidade.length > 0 ||
        selectedColumnFilters.bairro.length > 0 ||
        selectedColumnFilters.cliente.length > 0 ||
        selectedColumnFilters.tipoOS.length > 0 ||
        selectedColumnFilters.sla.length > 0 ||
        selectedColumnFilters.equipamento.length > 0 ||
        selectedColumnFilters.status.length > 0 ||
        (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) ||
        selectedColumnFilters.defeitoDescricao.length > 0 ||
        selectedColumnFilters.defeitoAlert.length > 0 ||
        selectedColumnFilters.defeitoClassification.length > 0 ||
        selectedColumnFilters.defeitoSeverity.length > 0
      );
    };

    const getFilterInfoMessage = () => {
      if (filterType === 'cidade') {
        const otherFilters = [];
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas cidades dos ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'bairro') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas bairros das ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'cliente') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas clientes das ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'tipoOS') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas tipos de OS das ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'equipamento') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas equipamentos dos ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'sla') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas SLAs dos ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'status') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.tecnicoInicial && selectedColumnFilters.tecnicoInicial.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tecnicoInicial.length} tÃ©cnico(s) inicial(is)`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas status dos ${otherFilters.join(' e ')} selecionado(s)`;
        }
      } else if (filterType === 'tecnicoInicial') {
        const otherFilters = [];
        if (selectedColumnFilters.cidade.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cidade.length} cidade(s)`);
        }
        if (selectedColumnFilters.bairro.length > 0) {
          otherFilters.push(`${selectedColumnFilters.bairro.length} bairro(s)`);
        }
        if (selectedColumnFilters.cliente.length > 0) {
          otherFilters.push(`${selectedColumnFilters.cliente.length} cliente(s)`);
        }
        if (selectedColumnFilters.tipoOS.length > 0) {
          otherFilters.push(`${selectedColumnFilters.tipoOS.length} tipo(s) de OS`);
        }
        if (selectedColumnFilters.sla.length > 0) {
          otherFilters.push(`${selectedColumnFilters.sla.length} SLA(s)`);
        }
        if (selectedColumnFilters.equipamento.length > 0) {
          otherFilters.push(`${selectedColumnFilters.equipamento.length} equipamento(s)`);
        }
        if (selectedColumnFilters.status.length > 0) {
          otherFilters.push(`${selectedColumnFilters.status.length} status`);
        }
        if (otherFilters.length > 0) {
          return `Exibindo apenas tÃ©cnicos iniciais dos ${otherFilters.join(' e ')} selecionado(s)`;
        }
      }
      return null;
    };

    const isPopover = variant === 'popover';

    if (isPopover) {
      return (
        <BoardSearchFilterPopoverContent
          filterType={filterType}
          tempFilters={tempFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          applyFilters={applyFilters}
          clearFilters={clearFilters}
          filteredBairros={filteredBairros}
          filteredClientes={filteredClientes}
          filteredEquipamentos={filteredEquipamentos}
          filteredStatus={filteredStatus}
          filteredTecnicoInicial={filteredTecnicoInicial}
          filteredDefeitoDescricao={filteredDefeitoDescricao}
          filteredDefeitoAlert={filteredDefeitoAlert}
          filteredDefeitoClassification={filteredDefeitoClassification}
          filteredDefeitoSeverity={filteredDefeitoSeverity}
          filteredStateTree={filteredStateTree}
          expandedEstados={expandedEstados}
          setExpandedEstados={setExpandedEstados}
          citiesByEstado={citiesByEstado}
          tiposOSWithCounts={tiposOSWithCounts}
          slasWithCounts={slasWithCounts}
          toggleBairroFilter={toggleBairroFilter}
          toggleClienteFilter={toggleClienteFilter}
          toggleEquipamentoFilter={toggleEquipamentoFilter}
          toggleStatusFilter={toggleStatusFilter}
          toggleTecnicoInicialFilter={toggleTecnicoInicialFilter}
          toggleTipoOSFilter={toggleTipoOSFilter}
          toggleSLAFilter={toggleSLAFilter}
          toggleCityFilter={toggleCityFilter}
          toggleEstadoFilter={toggleEstadoFilter}
          onSetCidades={setCidadeSelection}
          toggleDefeitoDescricaoFilter={toggleDefeitoDescricaoFilter}
          toggleDefeitoAlertFilter={toggleDefeitoAlertFilter}
          toggleDefeitoClassificationFilter={toggleDefeitoClassificationFilter}
          toggleDefeitoSeverityFilter={toggleDefeitoSeverityFilter}
          setTempFilters={setTempFilters}
          onApplyLive={onApplyLive}
          citiesWithCounts={citiesWithCounts}
        />
      );
    }

    return (
      <div
        className={[
          'column-filter-modal',
          'column-filter-modal--board',
          !isPopover &&
          (filterType === 'cliente' ||
            filterType === 'equipamento' ||
            filterType === 'defeitoDescricao' ||
            filterType === 'defeitoAlert' ||
            filterType === 'defeitoClassification' ||
            filterType === 'defeitoSeverity')
            ? 'column-filter-modal-wide'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {!isPopover && (
          <div className="column-filter-header">
            <div className="column-filter-header-left">
              <span className="column-filter-title">{getFilterTitle()}</span>
            </div>
            <button type="button" className="column-filter-close" onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </div>
        )}
        
        {filterType === 'retorno' && (
          <>
            <div className="column-filter-content">
              <div className="column-filter-options">
                <label className="column-filter-option">
                  <input
                    type="radio"
                    name="retorno-filter"
                    checked={tempFilters.retorno === 'sim'}
                    onChange={() => setTempFilters({ ...tempFilters, retorno: 'sim' })}
                  />
                  <span className="column-filter-option-content">
                    <span className="column-filter-option-text">Sim</span>
                    <span className="column-filter-option-desc">Apenas OS de retorno</span>
                  </span>
                </label>
                <label className="column-filter-option">
                  <input
                    type="radio"
                    name="retorno-filter"
                    checked={tempFilters.retorno === 'nao'}
                    onChange={() => setTempFilters({ ...tempFilters, retorno: 'nao' })}
                  />
                  <span className="column-filter-option-content">
                    <span className="column-filter-option-text">NÃ£o</span>
                    <span className="column-filter-option-desc">Excluir OS de retorno</span>
                  </span>
                </label>
              </div>
            </div>
            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar {tempFilters.retorno ? '(1)' : '(0)'}
              </button>
            </div>
          </>
        )}

        {filterType === 'cidade' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <input
                type="text"
                placeholder={`Buscar UF ou cidade... (${stateTree.reduce((acc, s) => acc + s.cities.length, 0)} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>

            <div className="column-filter-content">
              <div className="column-filter-options column-filter-tree">
                {filteredStateTree.length > 0 ? (
                  filteredStateTree.map(({ estado, cities }) => {
                    const isExpanded = expandedEstados.has(estado);
                    const stateCount = cities.reduce((acc, c) => acc + c.count, 0);
                      const stateCities = citiesByEstado[estado] || [];
                      const allStateCitiesSelected = stateCities.length > 0 && stateCities.every(c => tempFilters.cidade.includes(c));
                    return (
                      <div key={estado} className="column-filter-tree-group">
                        <button
                          type="button"
                          className="column-filter-tree-state"
                          onClick={() => setExpandedEstados(prev => {
                            const next = new Set(prev);
                            if (next.has(estado)) next.delete(estado);
                            else next.add(estado);
                            return next;
                          })}
                        >
                            <input
                              type="checkbox"
                              checked={allStateCitiesSelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleEstadoFilter(estado)}
                              aria-label={`Selecionar UF ${estado}`}
                            />
                          <span className="column-filter-tree-chevron" aria-hidden>
                            {isExpanded ? 'â–¼' : 'â–¶'}
                          </span>
                          <span className="column-filter-option-text">{estado}</span>
                          <span className="column-filter-option-count">{stateCount}</span>
                        </button>
                        {isExpanded && (
                          <div className="column-filter-tree-cities">
                            {cities.map(({ cidade, count }) => (
                              <label key={cidade} className="column-filter-option column-filter-tree-city">
                                <input
                                  type="checkbox"
                                  checked={tempFilters.cidade.includes(cidade)}
                                  onChange={() => toggleCityFilter(cidade)}
                                />
                                <span className="column-filter-option-content">
                                  <span className="column-filter-option-text">{cidade}</span>
                                  <span className="column-filter-option-count">{count}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="column-filter-no-results">
                    Nenhum resultado para &quot;{searchTerm}&quot;
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredStateTree.length > 0 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todas
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.cidade.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'bairro' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <input
                type="text"
                placeholder={`Buscar bairro... (${filteredBairros.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>

            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredBairros.length > 0 ? (
                  filteredBairros.map(({ bairro }) => (
                    <label key={bairro} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={tempFilters.bairro.includes(bairro)}
                        onChange={() => toggleBairroFilter(bairro)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{bairro}</span>
                        <span className="column-filter-option-count">{getBairroOrderCount(bairro)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    Nenhum bairro encontrado para "{searchTerm}"
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredBairros.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.bairro.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'cliente' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <input
                type="text"
                placeholder={`Buscar cliente... (${filteredClientes.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>

            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredClientes.length > 0 ? (
                  filteredClientes.map(({ cliente }) => (
                    <label key={cliente} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={tempFilters.cliente.includes(cliente)}
                        onChange={() => toggleClienteFilter(cliente)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{cliente}</span>
                        <span className="column-filter-option-count">{getClienteOrderCount(cliente)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    Nenhum cliente encontrado para "{searchTerm}"
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredClientes.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.cliente.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'tipoOS' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <div className="column-filter-info-message">
                <i className="bi bi-info-circle"></i>
                Tipos de OS disponÃ­veis ({tiposOSWithCounts.length} tipos)
              </div>
            </div>
            
            <div className="column-filter-content">
              <div className="column-filter-options">
                {tiposOSWithCounts.length > 0 ? (
                  tiposOSWithCounts.map(item => {
                    // Mapear as iniciais para nomes completos
                    const tipoNames = {
                      'C': 'Corretiva',
                      'I': 'InstalaÃ§Ã£o', 
                      'D': 'DesinstalaÃ§Ã£o',
                      'E': 'Estoque',
                      'S': 'Preventiva',
                      'B': 'BalcÃ£o',
                      'R': 'Retorno/Recarga',
                      'A': 'AferiÃ§Ã£o'
                    };
                    
                    const displayName = tipoNames[item.tipo] || item.tipo;
                    
                    return (
                      <label key={item.tipo} className="column-filter-option">
                        <input
                          type="checkbox"
                          checked={tempFilters.tipoOS.includes(item.tipo)}
                          onChange={() => toggleTipoOSFilter(item.tipo)}
                        />
                        <span className="column-filter-option-content">
                          <span className="column-filter-option-text">{displayName}</span>
                          <span className="column-filter-option-count">{item.count}</span>
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <div className="column-filter-no-results">
                    Nenhum tipo de OS encontrado
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {tiposOSWithCounts.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.tipoOS.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'sla' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <div className="column-filter-info-message">
                <i className="bi bi-info-circle"></i>
                SLAs disponÃ­veis ({slasWithCounts.length} tipos)
              </div>
            </div>
            
            <div className="column-filter-content">
              <div className="column-filter-options">
                {slasWithCounts.length > 0 ? (
                  slasWithCounts.map(item => {
                    // Mapear SLAs para nomes amigÃ¡veis
                    const slaNames = {
                      'vencido': 'Vencido',
                      'vencendo': 'Vencendo',
                      'ok': 'A vencer'
                    };
                    
                    const displayName = slaNames[item.sla] || item.sla;
                    
                    return (
                      <label key={item.sla} className="column-filter-option">
                        <input
                          type="checkbox"
                          checked={tempFilters.sla.includes(item.sla)}
                          onChange={() => toggleSLAFilter(item.sla)}
                        />
                        <span className="column-filter-option-content">
                          <span className="column-filter-option-text">{displayName}</span>
                          <span className="column-filter-option-count">{item.count}</span>
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <div className="column-filter-no-results">
                    Nenhum SLA encontrado
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {slasWithCounts.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.sla.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'equipamento' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <input
                type="text"
                placeholder={`Buscar equipamento... (${filteredEquipamentos.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>

            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredEquipamentos.length > 0 ? (
                  filteredEquipamentos.map(item => (
                    <label key={item.equipamento} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={tempFilters.equipamento.includes(item.equipamento)}
                        onChange={() => toggleEquipamentoFilter(item.equipamento)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.equipamento}</span>
                        <span className="column-filter-option-count">{getEquipamentoOrderCount(item.equipamento)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum equipamento encontrado para "${searchTerm}"` : 'Nenhum equipamento encontrado'}
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredEquipamentos.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.equipamento.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'status' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <input
                type="text"
                placeholder={`Buscar status... (${filteredStatus.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>

            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredStatus.length > 0 ? (
                  filteredStatus.map(item => (
                    <label key={item.status} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={tempFilters.status.includes(item.status)}
                        onChange={() => toggleStatusFilter(item.status)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.status}</span>
                        <span className="column-filter-option-count">{getStatusOrderCount(item.status)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum status encontrado para "${searchTerm}"` : 'Nenhum status encontrado'}
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredStatus.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.status.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'tecnicoInicial' && (
          <>
            <div className="column-filter-search-section">
              {getFilterInfoMessage() && (
                <div className="column-filter-info-message">
                  <i className="bi bi-info-circle"></i>
                  {getFilterInfoMessage()}
                </div>
              )}
              <input
                type="text"
                placeholder={`Buscar tÃ©cnico inicial... (${filteredTecnicoInicial.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>

            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredTecnicoInicial.length > 0 ? (
                  filteredTecnicoInicial.map(item => (
                    <label key={item.tecnicoInicial} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={tempFilters.tecnicoInicial.includes(item.tecnicoInicial)}
                        onChange={() => toggleTecnicoInicialFilter(item.tecnicoInicial)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.tecnicoInicial}</span>
                        <span className="column-filter-option-count">{getTecnicoInicialOrderCount(item.tecnicoInicial)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum tÃ©cnico inicial encontrado para "${searchTerm}"` : 'Nenhum tÃ©cnico inicial encontrado'}
                  </div>
                )}
              </div>
            </div>

            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredTecnicoInicial.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({tempFilters.tecnicoInicial.length})
              </button>
            </div>
          </>
        )}

        {filterType === 'defeitoDescricao' && (
          <>
            <div className="column-filter-search-section">
              <input
                type="text"
                placeholder={`Buscar descriÃ§Ã£o... (${filteredDefeitoDescricao.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>
            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredDefeitoDescricao.length > 0 ? (
                  filteredDefeitoDescricao.map((item) => (
                    <label key={item.value} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={td(tempFilters.defeitoDescricao).includes(item.value)}
                        onChange={() => toggleDefeitoDescricaoFilter(item.value)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.value}</span>
                        <span className="column-filter-option-count">{getDefeitoDescricaoCount(item.value)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum resultado para "${searchTerm}"` : 'Nenhuma descriÃ§Ã£o de defeito nos dados filtrados'}
                  </div>
                )}
              </div>
            </div>
            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredDefeitoDescricao.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({td(tempFilters.defeitoDescricao).length})
              </button>
            </div>
          </>
        )}

        {filterType === 'defeitoAlert' && (
          <>
            <div className="column-filter-search-section">
              <input
                type="text"
                placeholder={`Buscar cÃ³digo de alerta... (${filteredDefeitoAlert.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>
            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredDefeitoAlert.length > 0 ? (
                  filteredDefeitoAlert.map((item) => (
                    <label key={item.value} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={td(tempFilters.defeitoAlert).includes(item.value)}
                        onChange={() => toggleDefeitoAlertFilter(item.value)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.value}</span>
                        <span className="column-filter-option-count">{getDefeitoAlertCount(item.value)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum resultado para "${searchTerm}"` : 'Nenhum cÃ³digo de alerta nos dados filtrados'}
                  </div>
                )}
              </div>
            </div>
            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredDefeitoAlert.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({td(tempFilters.defeitoAlert).length})
              </button>
            </div>
          </>
        )}

        {filterType === 'defeitoClassification' && (
          <>
            <div className="column-filter-search-section">
              <input
                type="text"
                placeholder={`Buscar classificaÃ§Ã£o... (${filteredDefeitoClassification.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>
            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredDefeitoClassification.length > 0 ? (
                  filteredDefeitoClassification.map((item) => (
                    <label key={item.value} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={td(tempFilters.defeitoClassification).includes(item.value)}
                        onChange={() => toggleDefeitoClassificationFilter(item.value)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.value}</span>
                        <span className="column-filter-option-count">{getDefeitoClassificationCount(item.value)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum resultado para "${searchTerm}"` : 'Nenhuma classificaÃ§Ã£o nos dados filtrados'}
                  </div>
                )}
              </div>
            </div>
            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredDefeitoClassification.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({td(tempFilters.defeitoClassification).length})
              </button>
            </div>
          </>
        )}

        {filterType === 'defeitoSeverity' && (
          <>
            <div className="column-filter-search-section">
              <input
                type="text"
                placeholder={`Buscar criticidade... (${filteredDefeitoSeverity.length} disponÃ­veis)`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="column-filter-search-input"
                autoFocus
              />
            </div>
            <div className="column-filter-content">
              <div className="column-filter-options">
                {filteredDefeitoSeverity.length > 0 ? (
                  filteredDefeitoSeverity.map((item) => (
                    <label key={item.value} className="column-filter-option">
                      <input
                        type="checkbox"
                        checked={td(tempFilters.defeitoSeverity).includes(item.value)}
                        onChange={() => toggleDefeitoSeverityFilter(item.value)}
                      />
                      <span className="column-filter-option-content">
                        <span className="column-filter-option-text">{item.value}</span>
                        <span className="column-filter-option-count">{getDefeitoSeverityCount(item.value)}</span>
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="column-filter-no-results">
                    {searchTerm ? `Nenhum resultado para "${searchTerm}"` : 'Nenhuma criticidade nos dados filtrados'}
                  </div>
                )}
              </div>
            </div>
            <div className="column-filter-actions">
              <button className="column-filter-clear" onClick={clearFilters}>
                Limpar
              </button>
              {filteredDefeitoSeverity.length > 1 && (
                <button className="column-filter-select-all" onClick={selectAll}>
                  Selecionar Todos
                </button>
              )}
              <button className="column-filter-apply" onClick={applyFilters}>
                Aplicar ({td(tempFilters.defeitoSeverity).length})
              </button>
            </div>
          </>
        )}
      </div>
    );
  };
